import { DateTime, DateTimeFormatOptions } from "luxon";

import { IReferential } from "../modules/referential/reducers";

interface FullNameProps {
  firstname?: string;
  lastname?: string;
  startsWithLastname?: boolean;
}

export const capitalizeWord = (word: string) => {
  word = word.toLowerCase();
  return word.charAt(0).toUpperCase() + word.slice(1);
};

export const convertKeysToCamelCase = (obj: any): any => {
  if (Array.isArray(obj)) {
    return obj.map(convertKeysToCamelCase);
  } else if (typeof obj === "object" && obj !== null) {
    const camelCaseObj: { [key: string]: any } = {};
    for (const key in obj) {
      if (Object.prototype.hasOwnProperty.call(obj, key)) {
        const camelCaseKey = key.replace(/_([a-z])/g, (_match, letter) =>
          letter.toUpperCase()
        );
        camelCaseObj[camelCaseKey] = convertKeysToCamelCase(obj[key]);
      }
    }
    return camelCaseObj;
  }
  return obj;
};

export function getEnumValue<T extends Record<string, string>>(
  enumObj: T,
  key?: keyof T,
  defaultValue: string = "Inconnu(e)"
): string {
  if (key && enumObj[key]) {
    return enumObj[key];
  }
  return defaultValue;
}

export function getValue(
  value: any,
  defaultValue: string = "Inconnu(e)"
): string {
  return value ? value : defaultValue;
}

export function displayFullName(
  lastname?: string,
  firstname?: string,
  defaultValue: string = "Inconnu(e)"
): string {
  let fullName = defaultValue;
  if (lastname && !firstname) {
    fullName = capitalizeWord(lastname);
  } else if (!lastname && firstname) {
    fullName = capitalizeWord(firstname);
  } else if (firstname && lastname) {
    fullName = `${lastname?.toUpperCase()} ${capitalizeWord(firstname)}`;
  }
  return fullName;
}

export function getFullName({
  firstname,
  lastname,
  startsWithLastname,
}: FullNameProps) {
  if (firstname && lastname) {
    return startsWithLastname
      ? `${lastname?.trim()} ${firstname?.trim()}`
      : `${firstname?.trim()} ${lastname?.trim()}`;
  }

  if (firstname) {
    return firstname?.trim();
  }

  if (lastname) {
    return lastname?.trim();
  }
}

export function getFullNameWithFirstnameInitial({
  firstname,
  lastname,
}: FullNameProps) {
  if (firstname && lastname) {
    return `${firstname.trim().slice(0, 1)}. ${lastname.trim()} ${firstname.trim().slice(0, 1)} ${lastname.trim()}`;
  }

  if (firstname) {
    return firstname.trim().slice(0, 1);
  }

  if (lastname) {
    return lastname.trim();
  }
}

export function displayMinName(
  lastname?: string,
  firstname?: string,
  defaultValue: string = "Inconnu(e)"
): string {
  let minName = defaultValue;
  if (lastname && !firstname) {
    minName = capitalizeWord(lastname);
  } else if (!lastname && firstname) {
    minName = capitalizeWord(firstname);
  } else if (firstname && lastname) {
    minName = `${firstname.charAt(0).toUpperCase()}${firstname.trim() !== "" ? "." : ""}  ${capitalizeWord(
      lastname
    )}`;
  }
  return minName;
}

export function getReferentialLabel(
  referential?: IReferential,
  defaultValue: string = "Inconnu(e)"
): string {
  return referential ? referential.label : defaultValue;
}

export function getDateFromIso(
  date?: string,
  format: DateTimeFormatOptions = DateTime.DATE_SHORT,
  defaultValue: string = "."
): string {
  let formattedDate = defaultValue;
  if (date) {
    try {
      const dateTime = DateTime.fromISO(date);
      if (dateTime.isValid) {
        formattedDate = dateTime.toLocaleString(format);
      }
    } catch (e) {
      console.warn(`Error formatting date: ${date}`, e);
    }
  }
  return formattedDate;
}

/** Will add an "s" at the end of the word to pluralize given the value */
export function pluralize(
  value: number | null | undefined,
  wordToPluralize: string
) {
  if (value === null || value === undefined) return wordToPluralize;
  return wordToPluralize + (value > 1 ? "s" : "");
}

/**
 * Removes all whitespace from a string result
 * @param result - The string to process
 * @returns The string with all whitespace removed
 */
export function excludeWhitespace(result: string): string {
  if (!result) return result;
  return result.replace(/\s+/g, '');
}
